import React, { useState } from 'react';
import styled from 'styled-components';
import { FileText, Copy, Edit, Star, Download, Search, Filter, Plus, Bookmark, Clock, DollarSign, Users } from 'lucide-react';
import ContentLayout from '../../../Common/Component/ContentLayout';

const Container = styled.div`
  width: 100%;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  color: white;
  padding: 30px;
  border-radius: 12px;
  margin-bottom: 30px;
  text-align: center;
`;

const HeaderTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 10px 0;
`;

const HeaderDescription = styled.p`
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
`;

const FilterSection = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CreateButton = styled.button`
  margin-left: auto;
  padding: 8px 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  
  &:hover {
    background: #2563eb;
  }
`;

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const TemplateCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  overflow: hidden;
  transition: all 0.2s;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
  }
`;

const CardHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f3f4f6;
  position: relative;
`;

const PopularBadge = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background: #fbbf24;
  color: #92400e;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TemplateTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
`;

const TemplateCategory = styled.span`
  background: #e0f2fe;
  color: #0369a1;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
`;

const TemplateDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 10px 0 0 0;
  line-height: 1.4;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const TemplateStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 15px;
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const TemplatePreview = styled.div`
  background: #f9fafb;
  border-radius: 6px;
  padding: 12px;
  margin: 15px 0;
  font-size: 12px;
  color: #374151;
  line-height: 1.4;
  max-height: 80px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 20px;
    background: linear-gradient(transparent, #f9fafb);
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 15px;
`;

const ActionButton = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: ${props => props.primary ? '#3b82f6' : 'white'};
  color: ${props => props.primary ? 'white' : '#374151'};
  border: ${props => props.primary ? 'none' : '1px solid #d1d5db'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2563eb' : '#f3f4f6'};
  }
`;

const RatingStars = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
  margin-top: 8px;
`;

const StarIcon = styled.span`
  color: ${props => props.filled ? '#fbbf24' : '#d1d5db'};
  font-size: 12px;
`;

const RatingCount = styled.span`
  font-size: 11px;
  color: #6b7280;
  margin-left: 5px;
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #9ca3af;
`;

function QuoteTemplates() {
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('popular');
  const [searchTerm, setSearchTerm] = useState('');

  // 더미 견적 템플릿 데이터
  const templates = [
    {
      id: 'TPL-001',
      title: '기본 홈페이지 제작',
      category: '웹개발',
      description: '소규모 비즈니스를 위한 기본적인 홈페이지 제작 템플릿입니다.',
      usageCount: 245,
      rating: 4.8,
      reviewCount: 52,
      avgCost: 350,
      timeline: '3-4주',
      isPopular: true,
      preview: `프로젝트명: 기본 홈페이지 제작
카테고리: 웹개발
예상 기간: 3-4주
포함 사항:
- 반응형 웹디자인
- 기본 SEO 설정
- 문의 폼 구성
- CMS 연동
- 기본 유지보수`,
      features: ['반응형 디자인', 'SEO 최적화', 'CMS 연동', '문의 폼']
    },
    {
      id: 'TPL-002',
      title: '쇼핑몰 구축',
      category: '웹개발',
      description: '완전한 온라인 쇼핑몰을 위한 포괄적인 견적 템플릿입니다.',
      usageCount: 186,
      rating: 4.9,
      reviewCount: 38,
      avgCost: 850,
      timeline: '6-8주',
      isPopular: true,
      preview: `프로젝트명: 온라인 쇼핑몰 구축
카테고리: 웹개발
예상 기간: 6-8주
포함 사항:
- 상품 관리 시스템
- 주문 결제 시스템
- 회원 관리 기능
- 관리자 대시보드
- 배송 추적 시스템`,
      features: ['결제시스템', '재고관리', '회원관리', '관리자패널']
    },
    {
      id: 'TPL-003',
      title: '모바일 앱 개발',
      category: '앱개발',
      description: 'iOS/Android 크로스 플랫폼 모바일 앱 개발 템플릿입니다.',
      usageCount: 134,
      rating: 4.7,
      reviewCount: 29,
      avgCost: 1200,
      timeline: '8-12주',
      isPopular: false,
      preview: `프로젝트명: 모바일 앱 개발
카테고리: 앱개발  
예상 기간: 8-12주
포함 사항:
- iOS/Android 앱 개발
- 백엔드 API 구축
- 푸시 알림 기능
- 앱스토어 등록 지원
- 초기 유지보수`,
      features: ['크로스플랫폼', 'API개발', '푸시알림', '스토어등록']
    },
    {
      id: 'TPL-004',
      title: '브랜드 아이덴티티 디자인',
      category: '디자인',
      description: '로고부터 브랜딩까지 완전한 브랜드 아이덴티티 구축 템플릿입니다.',
      usageCount: 198,
      rating: 4.6,
      reviewCount: 44,
      avgCost: 280,
      timeline: '2-3주',
      isPopular: true,
      preview: `프로젝트명: 브랜드 아이덴티티 디자인
카테고리: 디자인
예상 기간: 2-3주
포함 사항:
- 로고 메인/서브 디자인
- 컬러 팔레트 정의
- 타이포그래피 가이드
- 명함/레터헤드 디자인
- 브랜드 가이드라인`,
      features: ['로고디자인', '컬러가이드', '인쇄물디자인', '가이드라인']
    },
    {
      id: 'TPL-005',
      title: 'SEO 마케팅 컨설팅',
      category: '마케팅',
      description: '검색 최적화를 통한 온라인 마케팅 전략 수립 템플릿입니다.',
      usageCount: 167,
      rating: 4.5,
      reviewCount: 31,
      avgCost: 180,
      timeline: '4-6주',
      isPopular: false,
      preview: `프로젝트명: SEO 마케팅 컨설팅
카테고리: 마케팅
예상 기간: 4-6주
포함 사항:
- 키워드 리서치
- 웹사이트 SEO 감사
- 콘텐츠 최적화 전략
- 백링크 구축 방안
- 월간 성과 리포트`,
      features: ['키워드분석', 'SEO감사', '콘텐츠전략', '성과분석']
    },
    {
      id: 'TPL-006',
      title: 'ERP 시스템 구축',
      category: '웹개발',
      description: '중소기업을 위한 통합 ERP 시스템 구축 템플릿입니다.',
      usageCount: 89,
      rating: 4.8,
      reviewCount: 16,
      avgCost: 2500,
      timeline: '12-16주',
      isPopular: false,
      preview: `프로젝트명: ERP 시스템 구축
카테고리: 웹개발
예상 기간: 12-16주
포함 사항:
- 인사 관리 모듈
- 회계 관리 시스템
- 재고 관리 기능
- 영업 관리 도구
- 보고서 대시보드`,
      features: ['인사관리', '회계시스템', '재고관리', '영업관리']
    }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    const matchesSearch = searchTerm === '' ||
      template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const sortedTemplates = [...filteredTemplates].sort((a, b) => {
    switch(sortBy) {
      case 'popular': return b.usageCount - a.usageCount;
      case 'rating': return b.rating - a.rating;
      case 'cost-low': return a.avgCost - b.avgCost;
      case 'cost-high': return b.avgCost - a.avgCost;
      case 'recent': return b.id.localeCompare(a.id);
      default: return 0;
    }
  });

  const handleTemplateUse = (template) => {
    // 실제로는 견적 요청 페이지로 이동하면서 템플릿 데이터를 전달
    console.log('Using template:', template.id);
    window.location.href = `/quote-request?template=${template.id}`;
  };

  const renderStars = (rating, reviewCount) => (
    <RatingStars>
      {[1, 2, 3, 4, 5].map(star => (
        <StarIcon key={star} filled={star <= Math.floor(rating)}>★</StarIcon>
      ))}
      <RatingCount>({reviewCount})</RatingCount>
    </RatingStars>
  );

  return (
    <ContentLayout 
      title="견적 템플릿"
      subtitle="검증된 견적 템플릿을 활용하여 더 빠르고 정확하게 견적을 요청하세요"
    >
      <Container>
        <HeaderSection>
          <HeaderTitle>견적 템플릿 라이브러리</HeaderTitle>
          <HeaderDescription>
            다른 고객들이 성공적으로 진행한 프로젝트 템플릿을 참고하여 
            더 정확한 견적을 요청할 수 있습니다
          </HeaderDescription>
        </HeaderSection>

        <FilterSection>
          <FilterSelect 
            value={categoryFilter} 
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">전체 카테고리</option>
            <option value="웹개발">웹개발</option>
            <option value="앱개발">앱개발</option>
            <option value="디자인">디자인</option>
            <option value="마케팅">마케팅</option>
            <option value="컨설팅">컨설팅</option>
          </FilterSelect>

          <FilterSelect 
            value={sortBy} 
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="popular">인기순</option>
            <option value="rating">평점순</option>
            <option value="recent">최신순</option>
            <option value="cost-low">낮은비용순</option>
            <option value="cost-high">높은비용순</option>
          </FilterSelect>

          <SearchInput
            type="text"
            placeholder="템플릿 제목, 설명 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <CreateButton>
            <Plus size={14} />
            나만의 템플릿 만들기
          </CreateButton>
        </FilterSection>

        <TemplateGrid>
          {sortedTemplates.length > 0 ? (
            sortedTemplates.map(template => (
              <TemplateCard key={template.id} onClick={() => handleTemplateUse(template)}>
                <CardHeader>
                  {template.isPopular && (
                    <PopularBadge>
                      <Star size={10} />
                      인기
                    </PopularBadge>
                  )}
                  <TemplateTitle>{template.title}</TemplateTitle>
                  <TemplateCategory>{template.category}</TemplateCategory>
                  <TemplateDescription>{template.description}</TemplateDescription>
                  {renderStars(template.rating, template.reviewCount)}
                </CardHeader>

                <CardContent>
                  <TemplateStats>
                    <StatItem>
                      <StatValue>{template.usageCount}</StatValue>
                      <StatLabel>사용횟수</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{template.avgCost}만원</StatValue>
                      <StatLabel>평균비용</StatLabel>
                    </StatItem>
                    <StatItem>
                      <StatValue>{template.timeline}</StatValue>
                      <StatLabel>예상기간</StatLabel>
                    </StatItem>
                  </TemplateStats>

                  <TemplatePreview>
                    {template.preview}
                  </TemplatePreview>

                  <div style={{marginBottom: '15px'}}>
                    <div style={{fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '8px'}}>
                      주요 기능
                    </div>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                      {template.features.map((feature, index) => (
                        <span key={index} style={{
                          background: '#f0f9ff',
                          color: '#0369a1',
                          padding: '3px 6px',
                          borderRadius: '8px',
                          fontSize: '10px'
                        }}>
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>

                  <CardActions>
                    <ActionButton primary>
                      <Copy size={12} />
                      이 템플릿 사용
                    </ActionButton>
                    <ActionButton>
                      <Bookmark size={12} />
                      저장
                    </ActionButton>
                    <ActionButton>
                      <Download size={12} />
                      다운로드
                    </ActionButton>
                  </CardActions>
                </CardContent>
              </TemplateCard>
            ))
          ) : (
            <EmptyState>
              <EmptyIcon>
                <FileText size={40} />
              </EmptyIcon>
              <h3 style={{margin: '0 0 10px 0', color: '#374151'}}>
                검색 결과가 없습니다
              </h3>
              <p style={{margin: 0, color: '#6b7280'}}>
                검색 조건을 변경하거나 다른 키워드로 시도해보세요
              </p>
            </EmptyState>
          )}
        </TemplateGrid>
      </Container>
    </ContentLayout>
  );
}

export default QuoteTemplates;